import { countLabel } from '@/utils/plural'
import EmptyState, { EmptyStateButton } from '@components/EmptyState'
import PageLoader from '@components/PageLoader'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { useAuth } from '@context/AuthContext'
import { useTrip, useTripMembers } from '@hooks'
import { useAccommodations } from '@hooks/useAccommodations'
import { useCreateAccommodation } from '@hooks/useCreateAccommodation'
import { useUploadFiles } from '@hooks/useUploadFiles'
import { formatUtcInstant, parseUtcInstant } from '@lib/datetime'
import { GetUserType } from '@orbit/api-types'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { PageColumn, PageHeader, PageScroll } from '@components/Page'
import AddAccommodationModal, { AddAccommodationSubmitData } from './AddAccommodationModal'
import {
    AccommodationCard,
    AccommodationGrid,
    AccommodationName,
    AddButton,
    AvatarStack,
    DateBlock,
    DateLabel,
    DateRow,
    DateValue,
    GuestCount,
    GuestRow,
    LoadingContainer,
    StackedAvatar,
    YoureAGuestPill,
} from './AccommodationsPage.styles'
import { ErrorNote } from '@components/Notice'

const MAX_VISIBLE_AVATARS = 4

const AccommodationsPage: React.FC = () => {
    const { tripId } = useParams<{ tripId: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
    const { data: accommodationsData, isLoading: accommodationsLoading, error: accommodationsError } = useAccommodations(tripId)
    const { data: membersData } = useTripMembers(tripId)

    const createAccommodation = useCreateAccommodation()
    const uploadFiles = useUploadFiles()

    const [isAddModalOpen, setIsAddModalOpen] = useState(false)

    const members = membersData?.members ?? []
    const accommodations = accommodationsData?.accommodations ?? []

    useBreadcrumbs(
        trip
            ? [
                { label: trip.name, path: `/trips/${tripId}` },
                { label: 'Accommodations' },
            ]
            : []
    )

    const getMemberById = (userId: number): GetUserType | undefined => {
        return members.find(m => m.id === userId)
    }


    const sortedAccommodations = [...accommodations].sort((a, b) => {
        const dateA = parseUtcInstant(a.checkInDate)
        const dateB = parseUtcInstant(b.checkInDate)
        return dateA.getTime() - dateB.getTime()
    })

    const handleAddAccommodation = async (data: AddAccommodationSubmitData) => {
        if (!tripId) return
        const { pendingFiles, ...accommodationData } = data
        try {
            const created = await createAccommodation.mutateAsync({ tripId, accommodationData })
            if (pendingFiles && pendingFiles.length > 0) {
                try {
                    await uploadFiles.mutateAsync({
                        tripId,
                        files: pendingFiles,
                        resourceType: 'ACCOMMODATION',
                        resourceId: created.id,
                    })
                } catch {
                    toast.error('Accommodation added, but attachments failed to upload.')
                }
            }
            toast.success('Accommodation added!')
            setIsAddModalOpen(false)
        } catch {
            toast.error('Failed to add accommodation. Please try again.')
        }
    }

    if (tripLoading || accommodationsLoading) {
        return (
            <PageLoader label="Loading accommodations..." />
        )
    }

    if (tripError || accommodationsError) {
        return (
            <ErrorNote>
                Error loading accommodations: {tripError?.message || accommodationsError?.message}
            </ErrorNote>
        )
    }

    if (!trip) {
        return <LoadingContainer>Trip not found</LoadingContainer>
    }

    return (
        <PageScroll>
            {accommodations.length === 0 ? (
                <EmptyState
                    art="accommodations"
                    title="No accommodations yet"
                    description="Hotels, rentals, and stays you add to this trip will show up here."
                >
                    <EmptyStateButton onClick={() => setIsAddModalOpen(true)}>
                        Add Accommodation
                    </EmptyStateButton>
                </EmptyState>
            ) : (
                <PageColumn>
                    <PageHeader
                        title="Accommodations"
                        subtitle={countLabel(accommodations.length, 'place')}
                        actions={
                            <AddButton onClick={() => setIsAddModalOpen(true)}>
                                + Add Accommodation
                            </AddButton>
                        }
                    />

                    <AccommodationGrid>
                        {sortedAccommodations.map(accommodation => {
                            const guests = accommodation.guests ?? []
                            const visibleGuests = guests.slice(0, MAX_VISIBLE_AVATARS)
                            const overflowCount = guests.length - MAX_VISIBLE_AVATARS
                            const isUserGuest = user ? guests.some(g => g.userId === user.id) : false

                            return (
                                <AccommodationCard
                                    key={accommodation.id}
                                    onClick={() => navigate(`/trips/${tripId}/accommodations/${accommodation.id}`)}
                                >
                                    <AccommodationName>{accommodation.name}</AccommodationName>

                                    <DateRow>
                                        <DateBlock>
                                            <DateLabel>Check-in</DateLabel>
                                            <DateValue>{formatUtcInstant(accommodation.checkInDate, 'datetime')}</DateValue>
                                        </DateBlock>
                                        <DateBlock $align="right">
                                            <DateLabel>Check-out</DateLabel>
                                            <DateValue>{formatUtcInstant(accommodation.checkOutDate, 'datetime')}</DateValue>
                                        </DateBlock>
                                    </DateRow>

                                    <GuestRow>
                                        <AvatarStack>
                                            {visibleGuests.map((guest, index) => {
                                                const member = getMemberById(guest.userId)
                                                if (!member) return null
                                                return (
                                                    <StackedAvatar key={guest.userId} $index={index} user={member} size="sm" ring />
                                                )
                                            })}
                                            {overflowCount > 0 && (
                                                <StackedAvatar $index={MAX_VISIBLE_AVATARS} size="sm" ring tone="gold" label={`+${overflowCount}`} />
                                            )}
                                        </AvatarStack>
                                        <GuestCount>
                                            {countLabel(guests.length, 'guest')}
                                        </GuestCount>
                                        {isUserGuest && (
                                            <YoureAGuestPill>You're a guest</YoureAGuestPill>
                                        )}
                                    </GuestRow>
                                </AccommodationCard>
                            )
                        })}
                    </AccommodationGrid>
                </PageColumn>
            )}

            <AddAccommodationModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={handleAddAccommodation}
                members={members}
                isSubmitting={createAccommodation.isPending}
            />
        </PageScroll>
    )
}

export default AccommodationsPage
